"use client"
import {
  Form,
  Formik,
  FormikHelpers,
  FormikProps,
  FormikValues,
  useFormikContext,
} from "formik"
import { ReactNode, Ref, useEffect } from "react"
import styled from "styled-components"

// --- REMOVED: import { ConnectedFocusError } from "focus-formik-error" ---

type CommonFormProps<Schema, Values> = {
  validationSchema: Schema
  initialValues: Values
  render(props: FormikProps<Values>): ReactNode
  onSubmit(values: Values, actions: FormikHelpers<Values>): Promise<void>
  innerRef?: Ref<FormikProps<Values>>
}

const StyledForm = styled(Form)`
  direction: rtl;
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 100%;
  }
`

// 1. New Custom Component: Handles focus safely after mount
const AutoFocusError = () => {
  const { isSubmitting, isValid, errors, submitCount } = useFormikContext()

  useEffect(() => {
    // Only run when submission is finished and there are errors
    if (submitCount > 0 && !isSubmitting && !isValid) {
      // Use setTimeout to defer focus to avoid state update during render
      const timeoutId = setTimeout(() => {
        const firstErrorKey = Object.keys(errors)[0]
        if (firstErrorKey) {
          // Find element by name attribute
          const errorElement = document.querySelector(
            `[name="${firstErrorKey}"]`
          ) as HTMLElement
          if (errorElement) {
            errorElement.focus()
            errorElement.scrollIntoView({ behavior: "smooth", block: "center" })
          }
        }
      }, 0)

      return () => clearTimeout(timeoutId)
    }
    // Only depend on submitCount - focus once per submission, not on every error change
  }, [submitCount, isSubmitting, isValid])

  return null
}

function FormikForm<Schema, Values extends FormikValues>({
  validationSchema,
  initialValues,
  render,
  onSubmit,
  innerRef,
}: CommonFormProps<Schema, Values>) {
  return (
    <Formik
      innerRef={innerRef as any}
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnBlur={true}
      validateOnChange={true}
      onSubmit={async (values, actions) => {
        actions.setSubmitting(true)
        await onSubmit(values, actions)
        actions.setSubmitting(false)
      }}
    >
      {(formik) => (
        <StyledForm noValidate>
          {/* 2. Use the safe component instead of ConnectedFocusError */}
          <AutoFocusError />
          {render(formik)}
        </StyledForm>
      )}
    </Formik>
  )
}

export default FormikForm
